import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Card, useTheme, Chip, Divider } from 'react-native-paper';
import { CheckCircle, AlertCircle, Clock, Zap } from 'lucide-react-native';

interface DashboardTestingSummaryProps {
  layout: string;
  skillLevel: string;
  journeyStage: string;
  interactionCount: number;
  testScenario: string;
  showExplanations: boolean;
  showConfidenceIndicators: boolean;
  maxItems: number;
  displayStyle: string;
}

export function DashboardTestingSummary({
  layout,
  skillLevel,
  journeyStage,
  interactionCount,
  testScenario,
  showExplanations,
  showConfidenceIndicators,
  maxItems,
  displayStyle,
}: DashboardTestingSummaryProps) {
  const theme = useTheme();

  const getLayoutIcon = () => {
    if (layout.includes('testing')) {
      return <Zap size={16} color={theme.colors.primary} />;
    }
    if (layout.includes('beginner')) {
      return <AlertCircle size={16} color={theme.colors.secondary} />;
    }
    if (layout.includes('advanced')) {
      return <CheckCircle size={16} color={theme.colors.tertiary} />;
    }
    return <Clock size={16} color={theme.colors.onSurfaceVariant} />;
  };

  const getScenarioColor = () => {
    switch (testScenario) {
      case 'new_user':
        return theme.colors.secondary;
      case 'power_user':
        return theme.colors.primary;
      case 'struggling_user':
        return theme.colors.error;
      case 'achievement_focused':
        return theme.colors.tertiary;
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  return (
    <Card style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <Card.Content style={styles.content}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            {getLayoutIcon()}
            <Text
              variant="titleMedium"
              style={[styles.title, { color: theme.colors.onSurface }]}
            >
              Testing Summary
            </Text>
          </View>
          <Chip
            style={[styles.scenarioChip, { backgroundColor: getScenarioColor() }]}
            textStyle={{ color: theme.colors.onPrimary }}
            compact
          >
            {testScenario.replace(/_/g, ' ')}
          </Chip>
        </View>

        <Divider style={styles.divider} />

        <View style={styles.metricsGrid}>
          <View style={styles.metric}>
            <Text
              variant="headlineSmall"
              style={[styles.metricValue, { color: theme.colors.primary }]}
            >
              {interactionCount}
            </Text>
            <Text
              variant="bodySmall"
              style={[styles.metricLabel, { color: theme.colors.onSurfaceVariant }]}
            >
              Interactions
            </Text>
          </View>

          <View style={styles.metric}>
            <Text
              variant="headlineSmall"
              style={[styles.metricValue, { color: theme.colors.secondary }]}
            >
              {maxItems}
            </Text>
            <Text
              variant="bodySmall"
              style={[styles.metricLabel, { color: theme.colors.onSurfaceVariant }]}
            >
              Max Items
            </Text>
          </View>

          <View style={styles.metric}>
            <Text
              variant="headlineSmall"
              style={[styles.metricValue, { color: theme.colors.tertiary }]}
            >
              {displayStyle}
            </Text>
            <Text
              variant="bodySmall"
              style={[styles.metricLabel, { color: theme.colors.onSurfaceVariant }]}
            >
              Style
            </Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        <View style={styles.configRow}>
          <View style={styles.configItem}>
            <Text
              variant="bodyMedium"
              style={[styles.configLabel, { color: theme.colors.onSurface }]}
            >
              Layout:
            </Text>
            <Chip compact style={styles.configChip}>
              {layout.replace(/_/g, ' ')}
            </Chip>
          </View>

          <View style={styles.configItem}>
            <Text
              variant="bodyMedium"
              style={[styles.configLabel, { color: theme.colors.onSurface }]}
            >
              User:
            </Text>
            <Chip compact style={styles.configChip}>
              {skillLevel} • {journeyStage}
            </Chip>
          </View>
        </View>

        <View style={styles.featuresRow}>
          <Chip
            icon={showExplanations ? 'check' : 'close'}
            compact
            style={[
              styles.featureChip,
              {
                backgroundColor: showExplanations
                  ? theme.colors.primaryContainer
                  : theme.colors.surfaceVariant,
              },
            ]}
          >
            Explanations
          </Chip>
          <Chip
            icon={showConfidenceIndicators ? 'check' : 'close'}
            compact
            style={[
              styles.featureChip,
              {
                backgroundColor: showConfidenceIndicators
                  ? theme.colors.primaryContainer
                  : theme.colors.surfaceVariant,
              },
            ]}
          >
            Confidence
          </Chip>
        </View>
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  content: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: '600',
  },
  scenarioChip: {
    opacity: 0.9,
  },
  divider: {
    marginVertical: 12,
  },
  metricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginVertical: 8,
  },
  metric: {
    alignItems: 'center',
    flex: 1,
  },
  metricValue: {
    fontWeight: '600',
    marginBottom: 4,
  },
  metricLabel: {
    textAlign: 'center',
  },
  configRow: {
    gap: 8,
  },
  configItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  configLabel: {
    flex: 1,
  },
  configChip: {
    flex: 2,
  },
  featuresRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  featureChip: {
    flex: 1,
  },
});